import config from '../config';
import { auth } from './auth';

class UnauthorizedError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'UnauthorizedError';
    this.isUnauthorized = true;
  }
}

const sanitizeErrorMessage = (error) => {
  const message = String(error?.message || error || 'Unknown error');
  return message.replace(/Bearer\s+[\w-]+\.[\w-]+\.[\w-]+/gi, 'Bearer [REDACTED]');
};

const logApiError = (context, error) => {
  console.error(context, sanitizeErrorMessage(error));
};

const isDebugApiEnabled = () => {
  try {
    return localStorage.getItem('debugApi') === 'true';
  } catch {
    return false;
  }
};
// Function to handle unauthorized responses
const handleUnauthorized = () => {
  auth.logout();
  // Dispatch a custom event that the app can listen to
  window.dispatchEvent(new CustomEvent('auth:unauthorized', {
    detail: { message: 'Your session has expired. Please log in again.' }
  }));
};

// Function to check if response is unauthorized
const isUnauthorized = (response, data) => {
  return response.status === 401 || 
         (response.status === 200 && data?.success === false && data?.message === 'Invalid token');
};

// Function to handle API responses
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  let data = null;
  if (contentType.includes('application/json')) {
    const text = await response.text();
    if (text.trim()) {
      try {
        data = JSON.parse(text);
      } catch (e) {
        const err = new Error(`Invalid JSON response: ${e.message}`);
        err.statusCode = response.status;
        throw err;
      }
    }
  }
  if (isUnauthorized(response, data)) {
    handleUnauthorized();
    throw new UnauthorizedError('Session expired');
  }
  if (!response.ok) {
    // Create error object that preserves backend error details
    const error = new Error(data?.error || 'API request failed');
    error.statusCode = response.status;
    // Preserve the results object from backend if present
    if (data?.results) {
      error.results = data.results;
    }
    // Mark 503 errors as non-retryable
    if (response.status === 503) {
      error.noRetry = true;
    }
    throw error;
  }
  return data;
};

const buildQueryString = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item === null || item === undefined || item === '') return;
        sp.append(k, String(item));
      });
      return;
    }
    sp.set(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const externalSignal = options.signal;
  if (externalSignal) {
    if (externalSignal.aborted) {
      const err = new Error('Request aborted');
      err.name = 'AbortError';
      err.isAbort = true;
      throw err;
    }
    externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const inflightRequests = new Map();

const isRetryableError = (error) => {
  if (!error) return false;
  if (error.isUnauthorized || error.noRetry) return false;
  if (error.statusCode && error.statusCode < 500 && error.statusCode !== 429) return false;
  if (error.name === 'AbortError') return true;
  if (error.message === 'Failed to fetch' || error.name === 'TypeError' || error.isNetworkError) return true;
  return !!error.statusCode;
};

const apiRequest = async ({
  url,
  method = 'GET',
  headers = {},
  body,
  timeoutMs = 30000,
  signal,
  retry = 2,
  dedup = method === 'GET',
}) => {
  const token = await auth.ensureFreshToken();
  if (!token) {
    auth.logout();
    throw new Error('No authentication token available');
  }
  const requestKey = `${method}:${url}:${body || ''}`;
  if (dedup && inflightRequests.has(requestKey)) {
    return inflightRequests.get(requestKey);
  }
  const runner = async () => {
    let attempt = 0;
    while (true) {
      try {
        if (isDebugApiEnabled()) {
          console.log('[API] Request', { method, url, body: body || null });
        }
        const response = await fetchWithTimeout(
          url,
          {
            method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              ...headers,
            },
            ...(body ? { body } : {}),
            ...(signal ? { signal } : {}),
          },
          timeoutMs
        );
        const data = await handleResponse(response);
        if (isDebugApiEnabled()) {
          console.log('[API] Response', { method, url, status: response.status, data });
        }
        return data;
      } catch (error) {
        if (error?.name === 'AbortError' && signal?.aborted) {
          const abortErr = new Error('Request aborted');
          abortErr.name = 'AbortError';
          abortErr.isAbort = true;
          throw abortErr;
        }
        if (isDebugApiEnabled()) {
          console.error('[API] Error', { method, url, error: sanitizeErrorMessage(error) });
        }
        if (!isRetryableError(error) || attempt >= retry) {
          if (error.name === 'AbortError') {
            const timeoutError = new Error('Request timeout: The server did not respond in time');
            timeoutError.isNetworkError = true;
            throw timeoutError;
          }
          if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
            const networkError = new Error(
              'Network error: Unable to reach the server. Please check your connection and ensure the backend is running.'
            );
            networkError.isNetworkError = true;
            throw networkError;
          }
          throw error;
        }
        const delay = 500 * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
        attempt += 1;
      }
    }
  };
  const promise = runner().finally(() => inflightRequests.delete(requestKey));
  if (dedup) inflightRequests.set(requestKey, promise);
  return promise;
};

const validateId = (value, label, { allowPath = false } = {}) => {
  const v = String(value || '').trim();
  if (!v) throw new Error(`${label} is required`);
  if (v.length > 200) throw new Error(`${label} is too long`);
  const pattern = allowPath ? /^[A-Za-z0-9_\-.:/]+$/ : /^[A-Za-z0-9_\-.:]+$/;
  if (!pattern.test(v)) throw new Error(`${label} has invalid characters`);
  return v;
};

const validateProjectId = (value) => {
  const v = String(value || '').trim();
  if (!v) throw new Error('Project ID is required');
  if (v.length > 200) throw new Error('Project ID is too long');
  if (!/^[A-Za-z0-9-]+$/.test(v)) throw new Error('Project ID has invalid characters');
  return v;
};

export const api = {
  /**
   * ENV-scoped policy catalog (env is inferred server-side from allowed projects).
   * GET {apiMonitoringUrl}/environments/policies
   */
  async getEnvPolicies({ includeDisabled = true, signal } = {}) {
    try {
      const url = `${config.apiMonitoringUrl}/environments/policies${buildQueryString({
        includeDisabled: includeDisabled ? '1' : '0',
      })}`;
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError('Error fetching monitoring policies:', error);
      throw error;
    }
  },

  /**
   * Projects that belong to the caller's env (requires the env marker in allowed projects).
   * GET {apiMonitoringUrl}/environments/projects
   */
  async getEnvProjects({ signal } = {}) {
    try {
      const url = `${config.apiMonitoringUrl}/environments/projects`;
      return await apiRequest({ url, method: 'GET', signal, retry: 0 });
    } catch (error) {
      logApiError('Error fetching environment projects:', error);
      throw error;
    }
  },

  async updatePolicyEnabled(policyId, enabled) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}/enabled`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify({ enabled }) });
    } catch (error) {
      logApiError(`Error updating policy ${policyId} enabled state:`, error);
      throw error;
    }
  },

  async updateProjectPolicyEnabled(policyId, enabled) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/projects/policies/${encodeURIComponent(safePolicyId)}/enabled`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify({ enabled }) });
    } catch (error) {
      logApiError(`Error updating project policy ${policyId} enabled state:`, error);
      throw error;
    }
  },

  async updatePolicyConditionQuery(policyId, conditionName, query) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      const safeConditionName = validateId(conditionName, 'Condition name', { allowPath: true });
      if (!query) throw new Error('Query is required');

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}/conditions/${encodeURIComponent(safeConditionName)}/query`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify({ query }) });
    } catch (error) {
      logApiError('Error updating policy condition query:', error);
      throw error;
    }
  },

  async updatePolicyConditionThreshold(policyId, conditionName, payload) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      const safeConditionName = validateId(conditionName, 'Condition name', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}/conditions/${encodeURIComponent(safeConditionName)}/threshold`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify(payload || {}) });
    } catch (error) {
      logApiError('Error updating policy condition threshold:', error);
      throw error;
    }
  },

  async updatePolicy(policyId, payload) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify(payload || {}) });
    } catch (error) {
      logApiError(`Error updating policy ${policyId}:`, error);
      throw error;
    }
  },

  async updateProjectPolicy(policyId, payload) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      const url = `${config.apiMonitoringUrl}/projects/policies/${encodeURIComponent(safePolicyId)}`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify(payload || {}) });
    } catch (error) {
      logApiError(`Error updating project policy ${policyId}:`, error);
      throw error;
    }
  },

  async updateProjectPolicyConditionQuery(policyId, conditionName, query) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      const safeConditionName = validateId(conditionName, 'Condition name', { allowPath: true });
      if (!query) throw new Error('Query is required');

      const url = `${config.apiMonitoringUrl}/projects/policies/${encodeURIComponent(safePolicyId)}/conditions/${encodeURIComponent(safeConditionName)}/query`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify({ query }) });
    } catch (error) {
      logApiError('Error updating project policy condition query:', error);
      throw error;
    }
  },

  async updateProjectPolicyConditionThreshold(policyId, conditionName, payload) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      const safeConditionName = validateId(conditionName, 'Condition name', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/projects/policies/${encodeURIComponent(safePolicyId)}/conditions/${encodeURIComponent(safeConditionName)}/threshold`;
      return await apiRequest({ url, method: 'PATCH', body: JSON.stringify(payload || {}) });
    } catch (error) {
      logApiError('Error updating project policy condition threshold:', error);
      throw error;
    }
  },

  async deletePolicy(policyId) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}`;
      return await apiRequest({ url, method: 'DELETE' });
    } catch (error) {
      logApiError(`Error deleting policy ${policyId}:`, error);
      throw error;
    }
  },

  async deleteProjectPolicy(policyId) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });

      const url = `${config.apiMonitoringUrl}/projects/policies/${encodeURIComponent(safePolicyId)}`;
      return await apiRequest({ url, method: 'DELETE' });
    } catch (error) {
      logApiError(`Error deleting project policy ${policyId}:`, error);
      throw error;
    }
  },

  async deleteAlerts(payload) {
    try {
      const params = {};
      const ids = Array.isArray(payload?.ids) ? payload.ids.map(String).map((s) => s.trim()).filter(Boolean) : [];
      if (ids.length > 0) params.ids = ids;
      if (payload?.component) params.component = payload.component;
      if (payload?.projectId) params.projectId = validateProjectId(payload.projectId);
      if (payload?.policyName) params.policyName = payload.policyName;
      if (payload?.incidentId) params.incidentId = payload.incidentId;
      if (payload?.startTime) params.startTime = payload.startTime;
      if (payload?.endTime) params.endTime = payload.endTime;
      if (payload?.limit) params.limit = payload.limit;

      const qs = buildQueryString(params);
      const deleteUrl = `${config.apiMonitoringUrl}/alerts${qs}`;

      if (ids.length === 1) {
        // Single ID: always use DELETE with query param(s).
        return await apiRequest({ url: deleteUrl, method: 'DELETE' });
      } else {
        // Multiple IDs OR category/time-range delete: always use POST body.
        return await apiRequest({
          url: `${config.apiMonitoringUrl}/alerts/delete`,
          method: 'POST',
          body: JSON.stringify(payload || {}),
        });
      }
    } catch (error) {
      logApiError('Error deleting alerts:', error);
      throw error;
    }
  },

  async getPolicyTemplates() {
    try {
      const url = `${config.apiMonitoringUrl}/policy-templates`;
      return await apiRequest({ url, method: 'GET' });
    } catch (error) {
      logApiError('Error fetching policy templates:', error);
      throw error;
    }
  },

  async createPolicy(policyData) {
    try {
      if (!policyData.component) throw new Error('Component is required');

      const url = `${config.apiMonitoringUrl}/environments/policies`;
      return await apiRequest({ url, method: 'POST', body: JSON.stringify(policyData) });
    } catch (error) {
      logApiError('Error creating policy:', error);
      throw error;
    }
  },

  async createPoliciesBatch(policies) {
    try {
      const url = `${config.apiMonitoringUrl}/environments/policies/batch`;
      return await apiRequest({
        url,
        method: 'POST',
        body: JSON.stringify({ policies }),
        retry: 0,
      });
    } catch (error) {
      logApiError('Error creating policies batch:', error);
      throw error;
    }
  },

  async getPolicyBatch(jobId, { signal } = {}) {
    try {
      const safeJobId = validateId(jobId, 'Job ID');
      const url = `${config.apiMonitoringUrl}/environments/policies/batch/${encodeURIComponent(safeJobId)}`;
      return await apiRequest({ url, method: 'GET', signal, retry: 0 });
    } catch (error) {
      logApiError(`Error fetching policy batch ${jobId}:`, error);
      throw error;
    }
  },

  async getNotificationChannels({ signal } = {}) {
    try {
      const url = `${config.apiMonitoringUrl}/notification-channels`;
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError('Error fetching notification channels:', error);
      throw error;
    }
  },

  async createEmailNotificationChannel(email) {
    try {
      if (!email || !email.includes('@')) throw new Error('Valid email is required');

      const url = `${config.apiMonitoringUrl}/notification-channels/email`;
      return await apiRequest({ url, method: 'POST', body: JSON.stringify({ email }) });
    } catch (error) {
      logApiError('Error creating email notification channel:', error);
      throw error;
    }
  },

  async subscribeToPolicy(policyId, email) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      if (!email || !email.includes('@')) {
        throw new Error('Valid email address is required');
      }

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}/subscribe`;
      return await apiRequest({ url, method: 'POST', body: JSON.stringify({ email }) });
    } catch (error) {
      logApiError(`Error subscribing ${email} to policy ${policyId}:`, error);
      throw error;
    }
  },

  async subscribeToProjectPolicy(policyId, email) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      if (!email) throw new Error('Email is required');

      const url = `${config.apiMonitoringUrl}/projects/policies/${encodeURIComponent(safePolicyId)}/subscribe`;
      return await apiRequest({ url, method: 'POST', body: JSON.stringify({ email }) });
    } catch (error) {
      logApiError(`Error subscribing to project policy ${policyId}:`, error);
      throw error;
    }
  },

  async unsubscribeFromPolicy(policyId, email) {
    try {
      const safePolicyId = validateId(policyId, 'Policy ID', { allowPath: true });
      if (!email || !email.includes('@')) {
        throw new Error('Valid email address is required');
      }

      const url = `${config.apiMonitoringUrl}/environments/policies/${encodeURIComponent(safePolicyId)}/unsubscribe`;
      return await apiRequest({ url, method: 'POST', body: JSON.stringify({ email }) });
    } catch (error) {
      logApiError(`Error unsubscribing ${email} from policy ${policyId}:`, error);
      throw error;
    }
  },

  /**
   * ENV-scoped alert history (env inferred server-side from allowed projects).
   * GET {apiMonitoringUrl}/environments/alerts?startTime=...&endTime=...&limit=...
   */
  async getEnvAlerts({ startTime, endTime, limit, signal } = {}) {
    try {
      const url = `${config.apiMonitoringUrl}/environments/alerts${buildQueryString({
        startTime,
        endTime,
        limit,
      })}`;
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError('Error fetching monitoring alerts:', error);
      throw error;
    }
  },

  async getProjectsAlerts({ startTime, endTime, limit, includeAllowedProjects = true, projectIds, signal } = {}) {
    try {
      const safeProjectIds = Array.isArray(projectIds)
        ? projectIds.filter(Boolean).map((id) => validateProjectId(id))
        : undefined;
      const url = `${config.apiMonitoringUrl}/projects/alerts${buildQueryString({
        startTime,
        endTime,
        limit,
        includeAllowedProjects: includeAllowedProjects ? '1' : '0',
        projectIds: safeProjectIds,
      })}`;
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError('Error fetching project-scoped monitoring alerts:', error);
      throw error;
    }
  },

  async getProjectAlerts(projectId, { startTime, endTime, limit, signal } = {}) {
    try {
      const safeProjectId = validateProjectId(projectId);
      const url = `${config.apiMonitoringUrl}/projects/${encodeURIComponent(safeProjectId)}/alerts${buildQueryString({
        startTime,
        endTime,
        limit,
      })}`;
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError(`Error fetching alerts for ${projectId}:`, error);
      throw error;
    }
  },

  async subscribeToProjectAlerts(projectId, email, topics = []) {
    try {
      const safeProjectId = validateProjectId(projectId);
      if (!email) {
        throw new Error('Email is required');
      }
      const env = config.getEnvironment?.() || localStorage.getItem('environment') || '';
      const url = `${config.apiMonitoringUrl}/projects/${encodeURIComponent(safeProjectId)}/alert-subscriptions${buildQueryString({
        env,
      })}`;
      return await apiRequest({ url, method: 'POST', body: JSON.stringify({ email, topics, env }) });
    } catch (error) {
      logApiError(`Error subscribing ${email} to alerts for ${projectId}:`, error);
      throw error;
    }
  },

  async getFactoryNetworkStatus(projectId = null, { signal } = {}) {
    try {
      let url = `${config.apiNmsUrl}/factory/network-status`;
      if (projectId) {
        url += `?project_id=${encodeURIComponent(validateProjectId(projectId))}`;
      }
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError('Error fetching factory network status:', error);
      throw error;
    }
  },

  async getFactoryLmsStatus(projectId = null, { signal } = {}) {
    try {
      let url = `${config.apiLmsUrl}/factory/projectsstats`;
      if (projectId) {
        url += `?project_id=${encodeURIComponent(validateProjectId(projectId))}`;
      }
      return await apiRequest({ url, method: 'GET', signal });
    } catch (error) {
      logApiError('Error fetching factory LMS status:', error);
      throw error;
    }
  },

  async getProjectLmsStatusById(projectId, { signal } = {}) {
    try {
      const safeProjectId = validateProjectId(projectId);

      const data = await this.getFactoryLmsStatus(safeProjectId, { signal });
      const normalizedProjectId = safeProjectId.toLowerCase();

      // API returns wrapper with projects as array
      if (Array.isArray(data?.projects)) {
        const found = data.projects.find(
          (proj) =>
            proj?.project_id &&
            (proj.project_id.toLowerCase() === normalizedProjectId || proj.project_id === projectId)
        );
        if (found) return found;
      }

      // Fallback: API might return single project object directly
      if (data?.project_id && (data.project_id.toLowerCase() === normalizedProjectId || data.project_id === projectId)) {
        return data;
      }

      throw new Error(`Project ${safeProjectId} not found in LMS response`);
    } catch (error) {
      logApiError(`Error fetching project LMS status for ${projectId}:`, error);
      throw error;
    }
  },

  async getProjectNetworkStatusById(projectId, { signal } = {}) {
    try {
      const safeProjectId = validateProjectId(projectId);
      const data = await this.getFactoryNetworkStatus(safeProjectId, { signal });
      
      // Extract the specific project from the response
      // The backend normalizes project IDs to lowercase
      const normalizedProjectId = safeProjectId.toLowerCase();
      
      if (data && data.projects) {
        // Try normalized (lowercase) first
        if (data.projects[normalizedProjectId]) {
          return data.projects[normalizedProjectId];
        }
        // Try original case as fallback
        if (data.projects[projectId]) {
          return data.projects[projectId];
        }
        // Try to find by matching project_id field in the response
        const foundProject = Object.values(data.projects).find(
          (proj) => proj.project_id && 
                   (proj.project_id.toLowerCase() === normalizedProjectId || 
                    proj.project_id === projectId)
        );
        if (foundProject) {
          return foundProject;
        }
      }
      
      throw new Error(`Project ${safeProjectId} not found in response`);
    } catch (error) {
      logApiError(`Error fetching project network status for ${projectId}:`, error);
      throw error;
    }
  }
  ,

  /**
   * Fetch sites (with GeoJSON location) via GraphQL get_sites.
   * NOTE: GraphQL endpoint is at {apiBaseUrl}/graphql (not /api/v1).
   */
  async getSites({ projectIds, signal } = {}) {
    const ids = Array.isArray(projectIds)
      ? projectIds.filter(Boolean).map((id) => validateProjectId(id))
      : [];

    // GraphQL query: get_sites(project_ids: [String!])
    const query = `
      query GetSites {
        get_sites {
          id
          name
          project_id
          zoom
          location
        }
      }
    `;

    const url = `${config.apiBaseUrl}/api/v1/graphql`;
    const data = await apiRequest({
      url,
      method: 'POST',
      body: JSON.stringify([
        {
          operationName: 'GetSites',
          query,
          variables: { project_ids: ids },
        },
      ]),
      dedup: false,
      signal,
    });

    // Support both batch ([{data,errors}]) and single ({data,errors}) responses
    const first = Array.isArray(data) ? data[0] : data;
    if (first?.errors?.length) {
      throw new Error(first.errors[0]?.message || 'GraphQL error fetching sites');
    }

    const sites = first?.data?.get_sites;
    return Array.isArray(sites) ? sites : [];
  },
};

